// SPDX-License-Identifier: Elastic-2.0
/**
 * PrivacySection — Settings section for the three privacy toggles
 * (T-028). Mirrors the A4 onboarding step but layers a batched-save
 * model on top: edits update local "draft" state, the Save button
 * lights up while the draft diverges from the server value, and
 * commits go through the `useSettings` mutation.
 *
 * Behaviour:
 *   - Initial draft = `query.data` once it lands.
 *   - Each toggle mutates the draft only (no per-toggle network call).
 *   - "Save changes" is disabled when the draft equals the server value
 *     OR while the mutation is in flight.
 *   - On success: success toast, draft + server value re-sync (handled
 *     by `useSettings`'s `setQueryData` on success).
 *   - On error: error toast; draft stays dirty so the user can retry.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Card } from "@/components/Card/Card";
import { Modal } from "@/components/Modal/Modal";
import { Toggle } from "@/components/Toggle";
import { useToast } from "@/components/Toast/useToast";

import { DEFAULT_PREFERENCES, requestTelemetryDelete, useSettings } from "../useSettings";
import type { Preferences } from "../useSettings";
import styles from "./PrivacySection.module.css";

type PrefKey = keyof Preferences;

const ROWS: ReadonlyArray<{ key: PrefKey; labelKey: string }> = [
  { key: "telemetry", labelKey: "settings.privacy.telemetry_label" },
  { key: "crash", labelKey: "settings.privacy.crash_label" },
  { key: "improve", labelKey: "settings.privacy.improve_label" },
];

/** Shallow-equal for the 3 boolean fields — cheap dirty check. */
function preferencesEqual(a: Preferences, b: Preferences): boolean {
  return a.telemetry === b.telemetry && a.crash === b.crash && a.improve === b.improve;
}

export function PrivacySection(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, mutation } = useSettings();

  // Draft state. Initialised from `DEFAULT_PREFERENCES`; once the query
  // resolves, we mirror the server value into the draft so the user
  // sees the live state. Subsequent server-side updates (other tab)
  // don't clobber a dirty draft — that's a v2 problem.
  const [draft, setDraft] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [hasSyncedFromQuery, setHasSyncedFromQuery] = useState(false);

  useEffect(() => {
    if (!hasSyncedFromQuery && query.data !== undefined) {
      setDraft(query.data);
      setHasSyncedFromQuery(true);
    }
  }, [query.data, hasSyncedFromQuery]);

  // When the mutation succeeds, the cache (`query.data`) flips to the
  // saved blob. We resync the draft so the dirty flag clears.
  useEffect(() => {
    if (mutation.isSuccess && query.data !== undefined) {
      setDraft(query.data);
    }
  }, [mutation.isSuccess, query.data]);

  // F-342: confirm modal shown when the user is turning telemetry OFF
  // (the only transition that triggers a retroactive delete).
  const [optOutModalOpen, setOptOutModalOpen] = useState(false);

  const serverValue = query.data ?? DEFAULT_PREFERENCES;
  const isDirty = useMemo(() => !preferencesEqual(draft, serverValue), [draft, serverValue]);
  const isSaving = mutation.isPending;
  const saveDisabled = !isDirty || isSaving || query.isLoading;

  // True when this Save would flip telemetry from on (server) to off
  // (draft) — that's the case that needs the "delete past data?" confirm.
  const isTelemetryOptOut = serverValue.telemetry && !draft.telemetry;

  function handleToggle(key: PrefKey, value: boolean): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  /** Persist the current draft; on telemetry opt-out, also fire the delete. */
  function commitSave(optingOut: boolean): void {
    mutation.mutate(draft, {
      onSuccess: () => {
        toast.show({ kind: "success", message: t("settings.privacy.save_success") });
        if (optingOut) {
          // Best-effort retroactive delete; never blocks the success path.
          void requestTelemetryDelete();
        }
      },
      onError: () => {
        toast.show({ kind: "error", message: t("settings.privacy.save_error") });
      },
    });
  }

  function handleSave(): void {
    // Opting out of telemetry routes through the confirm modal first;
    // every other change saves immediately.
    if (isTelemetryOptOut) {
      setOptOutModalOpen(true);
      return;
    }
    commitSave(false);
  }

  function handleOptOutConfirm(): void {
    setOptOutModalOpen(false);
    commitSave(true);
  }

  function handleOptOutCancel(): void {
    // Cancel reverts the telemetry toggle back to the server value
    // (still on) so the draft is no longer a pending opt-out.
    setOptOutModalOpen(false);
    setDraft((prev) => ({ ...prev, telemetry: serverValue.telemetry }));
  }

  return (
    <Card as="section" variant="elevated" padding="md" data-testid="settings-privacy">
      <h2 className={styles.title}>{t("settings.privacy.section_title")}</h2>

      <div className={styles.rows}>
        {ROWS.map((row) => (
          <div key={row.key} className={styles.row} data-testid={`settings-privacy-row-${row.key}`}>
            <Toggle
              checked={draft[row.key]}
              onChange={(value) => handleToggle(row.key, value)}
              label={t(row.labelKey)}
              disabled={query.isLoading}
            />
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={saveDisabled}
          data-testid="settings-privacy-save"
        >
          {t("settings.privacy.save")}
        </Button>
      </div>

      <Modal
        open={optOutModalOpen}
        onClose={handleOptOutCancel}
        title={t("settings.privacy.optout_confirm_title")}
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={handleOptOutCancel}
              data-testid="settings-privacy-optout-cancel"
            >
              {t("settings.privacy.optout_confirm_cancel")}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleOptOutConfirm}
              data-testid="settings-privacy-optout-confirm"
            >
              {t("settings.privacy.optout_confirm_accept")}
            </Button>
          </>
        }
      >
        {t("settings.privacy.optout_confirm_body")}
      </Modal>
    </Card>
  );
}

export default PrivacySection;
